import React, { useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, Phone, Mail, Anchor, Home, UtensilsCrossed, Beer, Calendar, Image as ImageIcon, FileText, X } from 'lucide-react';

// Import pages
import HomePage from './pages/Home';
import Sluzby from './pages/Sluzby';
import Obcerstveni from './pages/Obcerstveni';
import Jidlo from './pages/Jidlo';
import Akce from './pages/Akce';
import Fotogalerie from './pages/Fotogalerie';
import ProvozniRad from './pages/ProvozniRad';
import Kontakt from './pages/Kontakt';

// Import gallery pages
import PaleniCarodejnic from './pages/galerie/PaleniCarodejnic';
import DetskyDen from './pages/galerie/DetskyDen';
import VodoLodniSoutez from './pages/galerie/VodoLodniSoutez';
import KotlikovyGulas from './pages/galerie/KotlikovyGulas';
import UkonceniPrazdnin from './pages/galerie/UkonceniPrazdnin';

// Layout component
function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { id: 'uvod', label: 'Úvod', icon: Home, path: '/' },
    { id: 'sluzby', label: 'Služby', icon: Anchor, path: '/sluzby' },
    { id: 'obcerstveni', label: 'Občerstvení', icon: Beer, path: '/obcerstveni' },
    { id: 'jidlo', label: 'Jídlo', icon: UtensilsCrossed, path: '/jidlo' },
    { id: 'akce', label: 'Akce', icon: Calendar, path: '/akce' },
    { id: 'fotogalerie', label: 'Fotogalerie', icon: ImageIcon, path: '/fotogalerie' },
    { id: 'provozni-rad', label: 'Provozní řád', icon: FileText, path: '/provozni-rad' },
    { id: 'kontakt', label: 'Kontakt', icon: Phone, path: '/kontakt' },
  ];

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar Navigation */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-gray-900 text-white fixed h-full overflow-y-auto sidebar-pattern">
        <div className="p-6">
          <h1 className="text-3xl font-bold text-white leading-tight">Pískač</h1>
          <h1 className="text-3xl font-bold text-white leading-tight">Postrelmov</h1>
        </div>

        <nav className="flex-1 px-4">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.id === 'fotogalerie' 
                ? location.pathname.startsWith('/fotogalerie')
                : location.pathname === item.path;
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-800 text-sm text-gray-400">
          <p className="mb-2">© Pískač Postrelmov</p>
        </div>
      </aside>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 bg-gray-900 text-white p-3 rounded-lg shadow-lg"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-gray-900 text-white z-40 overflow-y-auto">
          <div className="pt-20 px-4">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white leading-tight">Pískač</h1>
              <h1 className="text-3xl font-bold text-white leading-tight">Postrelmov</h1>
            </div>
            <nav>
              <ul className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === 'fotogalerie' 
                    ? location.pathname.startsWith('/fotogalerie')
                    : location.pathname === item.path;
                  return (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left ${
                          isActive ? 'bg-blue-600' : 'hover:bg-gray-800'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </div>
      )}

      {/* Main Content - Opraveno do stabilní podoby */}
      <main className="flex-1 lg:ml-64">
        {/* Top Contact Bar */}
        <div className="bg-gray-800 text-white py-2 px-4 text-sm sticky top-0 z-30">
          <div className="container mx-auto flex flex-wrap justify-end items-center gap-4">
            <a href="mailto:piskacpostrelmov@seznam.cz" className="flex items-center gap-2 hover:text-blue-400">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">piskacpostrelmov@seznam.cz</span>
            </a>
            <a href="tel:605734830" className="flex items-center gap-2 hover:text-blue-400">
              <Phone className="w-4 h-4" />
              <span>605 734 830</span>
            </a>
          </div>
        </div>

        {/* Page Content */}
        {children}
      </main>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/sluzby" element={<Sluzby />} />
          <Route path="/obcerstveni" element={<Obcerstveni />} />
          <Route path="/jidlo" element={<Jidlo />} />
          <Route path="/akce" element={<Akce />} />
          <Route path="/fotogalerie" element={<Fotogalerie />} />
          <Route path="/fotogalerie/paleni-carodejnic" element={<PaleniCarodejnic />} />
          <Route path="/fotogalerie/detsky-den" element={<DetskyDen />} />
          <Route path="/fotogalerie/vodo-lodni-soutez" element={<VodoLodniSoutez />} />
          <Route path="/fotogalerie/kotlikovy-gulas" element={<KotlikovyGulas />} />
          <Route path="/fotogalerie/ukonceni-prazdnin" element={<UkonceniPrazdnin />} />
          <Route path="/provozni-rad" element={<ProvozniRad />} />
          <Route path="/kontakt" element={<Kontakt />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
